import React, { useEffect, useState } from 'react';

interface InlineSvgProps {
  url: string;
  className?: string;
  fallback?: React.ReactNode;
}

// Component to render SVG inline so it can inherit currentColor
export const InlineSvg: React.FC<InlineSvgProps> = ({ url, className, fallback }) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSvgContent(null);
    setFailed(false);
    fetch(url)
      .then(res => res.text())
      .then(text => {
        if (cancelled) return;
        // Parse and clean up the SVG
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'image/svg+xml');
        const svg = doc.querySelector('svg');
        if (!svg) {
          setFailed(true);
          return;
        }
        // Set standard size and ensure currentColor works
        svg.setAttribute('width', '20');
        svg.setAttribute('height', '20');
        // Remove full-viewBox background rects (SVG export artifacts)
        const vb = svg.getAttribute('viewBox')?.split(/\s+/).map(Number);
        if (vb && vb.length === 4) {
          svg.querySelectorAll(':scope > rect').forEach(rect => {
            const x = Number(rect.getAttribute('x') || 0);
            const y = Number(rect.getAttribute('y') || 0);
            const w = Number(rect.getAttribute('width') || 0);
            const h = Number(rect.getAttribute('height') || 0);
            if (x <= vb[0] && y <= vb[1] && w >= vb[2] && h >= vb[3]) {
              rect.remove();
            }
          });
        }

        // Detect placeholder SVGs: if no element has a stroke attribute
        // the icon is fill-only and won't render well as monochrome.
        const hasStrokes = svg.querySelector('[stroke]') !== null;
        if (!hasStrokes && fallback) {
          setFailed(true);
          return;
        }

        // Replace hardcoded colors with currentColor
        const elements = svg.querySelectorAll('[stroke]');
        elements.forEach(el => {
          const stroke = el.getAttribute('stroke');
          if (stroke && stroke !== 'none') {
            el.setAttribute('stroke', 'currentColor');
          }
        });
        const fillElements = svg.querySelectorAll('[fill]');
        fillElements.forEach(el => {
          const fill = el.getAttribute('fill');
          if (fill && fill !== 'none') {
            el.setAttribute('fill', 'currentColor');
          }
        });
        setSvgContent(svg.outerHTML);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => { cancelled = true; };
  }, [url, fallback]);

  if (failed && fallback) return <>{fallback}</>;
  if (!svgContent) return fallback ? <>{fallback}</> : null;

  return (
    <span
      className={className}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};
